import { createRouter, createWebHistory } from "vue-router";
import {JWTHelper} from "@/helpers/JWTHelper";
import {isChunkLoadError, lazyRoute, reloadForNewVersion, notifyNewVersion} from "@/helpers/lazyLoad";
import {captureOrgTarget} from "@/helpers/orgFromUrl";
import CheckAbilities from "@/views/CheckAbilities.vue";
import LoginPage from "@/views/LoginPage.vue";
const DashboardHandler = lazyRoute(() => import("@/views/components/DashboardHandler.vue"));
const UsersPage = lazyRoute(() => import("@/views/UsersPage.vue"));
const TablesPage = lazyRoute(() => import("@/views/TablesPage.vue"));
const MySettingsPage = lazyRoute(() => import("@/views/MySettingsPage.vue"));
const MyAccountPage = lazyRoute(() => import("@/views/MyAccountPage.vue"));
const ShiftPage = lazyRoute(() => import("@/views/ShiftPage.vue"));
const PaymentsPage = lazyRoute(() => import("@/views/PaymentsPage.vue"));
const OrderProductHistoryPage = lazyRoute(() => import("@/views/OrderProductHistoryPage.vue"));
const AccountSummaryPage = lazyRoute(() => import("@/views/AccountSummaryPage.vue"));
const AccountHistoryPage = lazyRoute(() => import("@/views/AccountHistoryPage.vue"));
const PrintersPage = lazyRoute(() => import("@/views/PrintersPage.vue"));
const OpenAccountsPage = lazyRoute(() => import("@/views/OpenAccountsPage.vue"));
const ShiftDetailPage = lazyRoute(() => import("@/views/ShiftDetailPage.vue"));
const ShiftStatPage = lazyRoute(() => import("@/views/ShiftStatPage.vue"));
const StockPage = lazyRoute(() => import("@/views/StockPage.vue"));
const ClientPage = lazyRoute(() => import("@/views/ClientPage.vue"));
const InvoicePage = lazyRoute(() => import("@/views/InvoicePage.vue"));
const WorkerShiftDetailPage = lazyRoute(() => import("@/views/WorkerShiftDetailPage.vue"));
const KitchenPage = lazyRoute(() => import("@/views/KitchenPage.vue"));
const MediaPage = lazyRoute(() => import("@/views/MediaPage.vue"));
const PromoPage = lazyRoute(() => import("@/views/PromoPage.vue"));
const PickupPage = lazyRoute(() => import("@/views/PickupPage.vue"));
const DeliveryAreasPage = lazyRoute(() => import("@/views/DeliveryAreasPage.vue"));
const ExtIntegrationPage = lazyRoute(() => import("@/views/ExtIntegrationPage.vue"));
const DevicePage = lazyRoute(() => import("@/views/DevicePage.vue"));
const CreateOrgPage = lazyRoute(() => import("@/views/CreateOrgPage.vue"));
const ProductAndGroupPage = lazyRoute(() => import("@/views/ProductAndGroupPage.vue"));
const ImportFilePage = lazyRoute(() => import("@/views/ImportFilePage.vue"));
const DigitalMenuPage = lazyRoute(() => import("@/views/DigitalMenuPage.vue"));
const CustomScene = lazyRoute(() => import("@/views/CustomScene.vue"));
const IconsPage = lazyRoute(() => import("@/views/IconsPage.vue"));
const ProductOptionsPage = lazyRoute(() => import("@/views/ProductOptionsPage.vue"));
const CatalogPage = lazyRoute(() => import("@/views/CatalogPage.vue"));
const SuperUsersPage = lazyRoute(() => import("@/views/SuperUsersPage.vue"));
const VouchersPage = lazyRoute(() => import("@/views/VouchersPage.vue"));


const routes = [
    {
    path: "/",
    name: "/",
    redirect: "/check_abilities",
  },
  {
    path: "/check_abilities",
    name: "CheckAbilities",
    component: CheckAbilities,
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardHandler,
  },
  {
    path: "/login",
    name: "LoginPage",
    component: LoginPage,
  },
  {
    path: "/users",
    name: "UsersPage",
    component: UsersPage,
  },
  {
    path: "/tables",
    name: "TablesPage",
    component: TablesPage,
  },
  {
    path: "/products/:productId?",
    name: "ProductsPage",
    redirect: (to) => ({
      name: "CatalogPage",
      query: {
        ...to.query,
        view: "products",
        ...(to.params.productId ? {product: String(to.params.productId)} : {}),
      },
    }),
  },
  {
    path: "/catalog/:productGroupId?",
    name: "CatalogPage",
    component: CatalogPage,
  },
  {
    path: "/product_groups/:productGroupId?",
    redirect: (to) => ({name: "CatalogPage", params: to.params, query: to.query}),
  },
  {
    path: "/product_options",
    name: "ProductOptions",
    component: ProductOptionsPage,
  },
  {
    path: "/kitchen",
    name: "Kitchen",
    component: KitchenPage,
  },
  {
    path: "/promo",
    name: "Promo",
    component: PromoPage,
  },
  {
    path: "/vouchers",
    name: "Vouchers",
    component: VouchersPage,
  },
  {
    path: "/pickup",
    name: "Pickup",
    component: PickupPage,
  },
  {
    path: "/delivery-areas",
    name: "DeliveryAreas",
    component: DeliveryAreasPage,
  },
  {
    path: "/media",
    name: "Media",
    component: MediaPage,
  },
  {
    path: "/icons",
    name: "Icons",
    component: IconsPage,
  },
  {
    path: "/external-integration",
    name: "ExtIntegration",
    component: ExtIntegrationPage,
  },
  {
    path: "/device",
    name: "Device",
    component: DevicePage,
  },
  {
    path: "/settings/:panel?",
    name: "MySettingsPage",
    component: MySettingsPage,
  },
  {
    path: "/me",
    name: "MyAccountPage",
    component: MyAccountPage,
  },
  {
    path: "/analytics/shifts",
    name: "ShiftPage",
    component: ShiftPage,
  },
  {
    path: "/analytics/payments",
    name: "AnalyticsPayments",
    component: PaymentsPage,
  },
  {
    path: "/analytics/orders",
    name: "AnalyticsOrders",
    component: OrderProductHistoryPage,
  },
  {
    path: "/account/:accountId",
    name: "AccountSummary",
    component: AccountSummaryPage,
    meta: {title: "generic.account", icon: "clock.svg"},
  },
  {
    path: "/analytics/accounts",
    name: "AccountsHistory",
    component: AccountHistoryPage,
  },
  {
    path: "/printers",
    name: "Printers",
    component: PrintersPage,
  },
  {
    path: "/open-accounts",
    name: "OpenAccounts",
    component: OpenAccountsPage,
    meta: {title: "openAccountsPage.header", icon: "table.svg"},
  },
  {
    path: "/open-tables",
    redirect: { name: "OpenAccounts", query: { type: "TABLE" } },
  },
  {
    path: "/open-takeaway",
    redirect: { name: "OpenAccounts", query: { type: "TAKEAWAY" } },
  },
  {
    path: "/shift-details/:shiftId",
    name: "ShiftDetailPage",
    component: ShiftDetailPage,
    meta: {title: "shiftDetailPage.header", icon: "activity.svg"},
  },
  {
    path: "/shift-stats/:shiftId",
    name: "ShiftStatPage",
    component: ShiftStatPage,
    meta: {title: "shiftStatPage.header", icon: "statistic.svg"},
  },
  {
    path: "/shift-stats/:startdate/:enddate",
    name: "ShiftDetailPageByDate",
    component: ShiftStatPage,
    meta: {title: "shiftStatPage.header", icon: "statistic.svg"},
  },
  {
    path: "/worker-shift-detail/:startdate/:enddate",
    name: "WorkerShiftDetailPage",
    component: WorkerShiftDetailPage,
    meta: {title: "generic.shiftUser", icon: "users.svg"},
  },
  {
    path: "/stock/:productId",
    name: "stock",
    component: StockPage,
    meta: {title: "navBar.stock", icon: "box.svg"},
  },
  {
    path: "/clients/:clientDataId?",
    name: "clients",
    component: ClientPage,
    props: true
  },
  {
    path: "/invoice",
    name: "invoice",
    component: InvoicePage,
  },
  {
    path: "/create-org",
    name: "create-org",
    component: CreateOrgPage
  },
  {
    path: "/menu",
    name: "menu",
    component: ProductAndGroupPage
  },
  {
    path: "/import-file",
    name: "ImportFile",
    component: ImportFilePage
  },
  {
    path: "/digital-menu",
    name: "DigitalMenu",
    component: DigitalMenuPage
  },
  {
      path: "/custom-scene",
      name: "CustomScene",
      component: CustomScene,
      meta: {title: "generic.digitalMenu", icon: "monitor.svg"}
  },
  {
    path: "/super-users",
    name: "SuperUsersPage",
    component: SuperUsersPage,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
});

router.beforeEach((to, from, next) => {
  const publicPages = ['/login'];
  const authRequired = !publicPages.includes(to.path);
  const loggedIn = JWTHelper.isLoggedIn();

  if (captureOrgTarget(to)) {
    return next({path: loggedIn ? '/check_abilities' : '/login', replace: true});
  }

  if (authRequired && !loggedIn) {
    return next('/login');
  }
  else if (to.path === '/login' && loggedIn){
    return next('/');
  }

  next();
});

router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    if (reloadForNewVersion(to?.fullPath)) return;
    notifyNewVersion();
  }
  console.error(error);
});


export default router;
